import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useDownloader from "react-use-downloader";

import { useCardAttachment } from "../../../../../../reactQuery/cards";
import {
  deleteCardAttachment,
  downloadCardAttachment,
} from "../../../../../../services/apiCards";
import { handleDownloadAttachment } from "../../../../../../utils/downloadAttachments";
import { useResponsive } from "../../../../../../hooks/useResponsive";
import useMutate from "../../../../../../hooks/useMutate";
import {
  getResponseData,
  getTotalRecord,
} from "../../../../../../reactQuery/response";

export function useDetailAttachmentsState({ boardId, cardId }) {
  const [deleteAttachment, setDeleteAttachment] = useState(false);
  const [deleteAttachmentId, setDeleteAttachmentId] = useState(null);
  const [showAttachmentSlideShow, setShowAttachmentSlideShow] = useState(false);
  const [attachmentId, setAttachmentId] = useState(null);
  const queryClient = useQueryClient();
  const { download } = useDownloader();
  const isMobile = useResponsive("down", "md");
  const [searchParams] = useSearchParams();
  const { cardAttachment } = useCardAttachment(cardId);
  const attachments = useMemo(
    () =>
      [...getResponseData(cardAttachment, [])].sort(
        (left, right) => left.card_attachment_id - right.card_attachment_id
      ),
    [cardAttachment]
  );
  const totalRecord = useMemo(
    () => getTotalRecord(cardAttachment, attachments.length),
    [attachments.length, cardAttachment]
  );

  const { mutate: deleteCardAttachmentMutate, isPending } = useMutate(
    deleteCardAttachment,
    [],
    () => {},
    () => {
      queryClient.invalidateQueries({
        queryKey: ["cardAttachment", cardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["history", String(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: ["feeds", String(boardId), searchParams.get("milestone")],
      });
      setDeleteAttachment(false);
    },
    () => {
      setDeleteAttachment(false);
    }
  );

  const handleDelete = useCallback((nextAttachmentId) => {
    setDeleteAttachmentId(nextAttachmentId);
    setDeleteAttachment(true);
  }, []);

  const handleSlideShowAttachment = useCallback((nextAttachmentId) => {
    setAttachmentId(nextAttachmentId);
    setShowAttachmentSlideShow(true);
  }, []);

  const handleView = useCallback((nextAttachmentId) => {
    downloadCardAttachment(nextAttachmentId)
      .then((response) => {
        const responseData = getResponseData(response, {});

        handleDownloadAttachment(
          responseData?.url,
          responseData?.attachment_name,
          window.open,
          true
        );
      })
      .catch(() => {});
  }, []);

  const handleDownload = useCallback(
    (nextAttachmentId) => {
      downloadCardAttachment(nextAttachmentId)
        .then((response) => {
          const responseData = getResponseData(response, {});
          const filename =
            responseData?.attachment_name ?? responseData?.filename;
          const fileUrl = responseData?.url;

          if (!filename || !fileUrl) {
            return;
          }

          const fileExtension = fileUrl.split(".").pop();
          const completeFilename = filename.endsWith(`.${fileExtension}`)
            ? filename
            : `${filename}.${fileExtension}`;

          handleDownloadAttachment(fileUrl, completeFilename, download);
        })
        .catch(() => {});
    },
    [download]
  );

  return {
    attachmentId,
    attachments,
    deleteAttachment,
    handleDelete,
    handleDeleteConfirm: () => deleteCardAttachmentMutate(deleteAttachmentId),
    handleDownload,
    handleSlideShowAttachment,
    handleView,
    isMobile,
    isPending,
    setDeleteAttachment,
    setShowAttachmentSlideShow,
    showAttachmentSlideShow,
    totalRecord,
  };
}
